import { useState } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { AdminSystemPermissions } from '@wajeez-web/core/types';
import {
  useProductSelector,
  ProductsActions,
} from '@wajeez-web/stores/product';
import { Button } from '@wajeez-web/components/ui';
import { PlusIcon } from '@heroicons/react/solid';
import { DataTableWrapper, Header } from '@wajeez-web/components/helpers';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { Server } from '@wajeez/wajeez.microservices';

import { ProductModal } from '../components/ProductModal';

const ProductsPage = () => {
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] =
    useState<Partial<Server.ProductGroupProductOutput>>();

  const datatable = useProductSelector(
    ({ products }) => products.productsDatatable
  );

  const { tablePreferances, setTablePreferences, isTableReferencesLoading } =
    HandleTableData('products', ProductsActions.getProductsDatatableAsync);

  return (
    <div>
      <PageLayoutsContainer
        title="Products"
        endSideComponent={[
          {
            element: (
              <Button
                title={'Add Product'}
                bg={'primary'}
                color={'white'}
                Icon={PlusIcon}
                onClick={() => {
                  setIsProductModalOpen(true);
                }}
              />
            ),
            permission: [AdminSystemPermissions.products.add],
          },
        ]}
        withoutRightPadding={true}
        breadCrumbsLinks={[{ title: 'Products' }]}
      >
        <DataTableWrapper
          data={datatable.data as contentEntityDataType}
          isLoading={datatable.status === 'loading' || isTableReferencesLoading}
          onHeaderManage={(header) => {
            const flags = header
              ?.filter((h) => h.selected)
              .reduce(
                (previousValue: number, currentValue: Header) =>
                  previousValue + Number(currentValue.value),
                0
              );

            tablePreferances &&
              flags &&
              setTablePreferences({
                ...tablePreferances,
                flags: flags + 1,
              });
          }}
          onPaginationChange={(pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
              });
          }}
          sort={tablePreferances?.sortingOperations?.[0]}
          onSort={(sort) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                sortingOperations: [sort],
              });
          }}
          onFilter={(filters, pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                filteringOperations: filters.map((f) => {
                  return {
                    property: Number(f.key),
                    cretiria: f.value,
                    comparisonType: f.operator,
                  };
                }),
              });
          }}
          rowActions={(row) => [
            {
              title: 'Edit',
              permission: AdminSystemPermissions.products.edit,
              type: 'request',
              endpoint: '',
              action: () => {
                setSelectedProduct({
                  id: Number(row[1]),
                });
                setIsProductModalOpen(true);
              },
            },
          ]}
          preferences={tablePreferances || undefined}
          setPreferences={setTablePreferences}
        />
      </PageLayoutsContainer>
      {isProductModalOpen && (
        <ProductModal
          isOpen={isProductModalOpen}
          onClose={() => {
            setIsProductModalOpen(false);
            setSelectedProduct(undefined);
          }}
          onSubmit={() => {
            setIsProductModalOpen(false);

            if (selectedProduct?.id) {
              setSelectedProduct(undefined);
            }

            tablePreferances && setTablePreferences({ ...tablePreferances });
          }}
          id={selectedProduct?.id}
        />
      )}
    </div>
  );
};

export default ProductsPage;
